/**
 * auth（02 3 章・5.3.1）の本文・応答。
 */
import { z } from 'zod';
import { zEmail } from './common.schema';

/**
 * 確認コード（8 桁の数字。MSG-V-086 未入力／MSG-V-008 形式違い：03 SCR-002）。
 * Cognito のメール OTP（パスワードレスのサインイン）が 8 桁で固定のため、local の自前発行も 8 桁にそろえた（実装メモ 16 章）。
 */
export const AUTH_CODE_LENGTH = 8;
export const zAuthCode = z
  .string({ message: 'MSG-V-086' })
  .min(1, 'MSG-V-086')
  .regex(/^\d{8}$/, 'MSG-V-008');

/** API-AUTH-01 の本文 */
export const AuthCodeRequestSchema = z.object({ email: zEmail }).strict();
/** API-AUTH-02 の本文 */
export const AuthVerifyRequestSchema = z.object({ email: zEmail, code: zAuthCode }).strict();
/** API-AUTH-06 の本文 */
export const EmailVerificationRequestSchema = z.object({ email: zEmail }).strict();

/** 型 Me（ログイン中の利用者） */
export const MeSchema = z.object({
  user_code: z.string(),
  email: z.string(),
  user_name: z.string(),
  affiliated_company_name: z.string().nullable(),
  role_code: z.string(),
  role_name: z.string(),
  permissions: z.array(z.string()),
  last_login_at: z.string().nullable(),
});

export type Me = z.infer<typeof MeSchema>;

export const AuthCodeResponseSchema = z.object({
  email: z.string(),
  expires_at: z.string(),
  resend_available_at: z.string(),
});

export const AuthVerifyResponseSchema = MeSchema.extend({
  access_token_expires_at: z.string(),
  refresh_token_expires_at: z.string(),
});

export const AuthRefreshResponseSchema = z.object({
  access_token_expires_at: z.string(),
  refresh_token_expires_at: z.string(),
});

export const AuthLogoutResponseSchema = z.object({ logged_out: z.literal(true) });

export const EmailVerificationResponseSchema = z.object({
  email: z.string(),
  verification_required: z.boolean(),
  already_verified: z.boolean(),
  sent: z.boolean(),
});

export const AuthTestCodeResponseSchema = z.object({
  email: z.string(),
  code: z.string(),
  expires_at: z.string(),
});

export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  env_name: z.string(),
  version: z.string(),
  checked_at: z.string(),
});

export type AuthCodeRequest = z.infer<typeof AuthCodeRequestSchema>;
export type AuthVerifyRequest = z.infer<typeof AuthVerifyRequestSchema>;
export type AuthCodeResponse = z.infer<typeof AuthCodeResponseSchema>;
export type AuthVerifyResponse = z.infer<typeof AuthVerifyResponseSchema>;
export type AuthRefreshResponse = z.infer<typeof AuthRefreshResponseSchema>;
export type EmailVerificationResponse = z.infer<typeof EmailVerificationResponseSchema>;
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
